import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatDateMedPadded } from '@cf-shared/date-format';
import { describeAuthRoles } from 'util/formatting';
import { UserDb } from 'services/user-db.service';
import { AuthService } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { SettingsService } from 'services/settings.service';
import { PopupMenuProvider, PopupMenuOptions } from 'providers/popup-menu/popup-menu';
import { AuthRole } from '@cf-shared/couch-auth';
import { DateTime } from 'luxon';
import { resolveTrialEndIso } from './subscription-trial-display';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ImageService } from 'services/image-service';
import { ImageUploadOutboxService } from 'services/image-upload-outbox.service';
import { NativeImageCacheService } from 'services/native-image-cache.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { ToastController } from 'providers/toast-controller';
import { presentSupportContactAsync } from 'utils/support-contact';
import { isCapacitorNative, getCapacitorPlatform } from 'environments/environment-helpers';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { BecomeCoachFlowService } from 'services/become-coach-flow.service';
import { getMyApp } from 'app/app-interface';
import { CsPage, NavController } from 'providers/nav-controller';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { LoggingService } from 'services/logging.service';
import { DatabaseRecoveryService } from 'services/database-recovery.service';
import { TelemetryService } from 'services/telemetry.service';
import { SubscriptionStatus, isPausedPaddleSubscriber } from '@cf-shared/data-model/couch-user';
import { SubscriptionCardModel, buildSubscriptionCardModel } from './subscription-card-model';
import { sleep, timeout } from '@cf-shared/shared-util';
import { errorReportFields } from 'services/error-patterns';
import { noteSomethingWentWrong } from 'services/session-health';
import { LoadingController } from 'providers/loading-controller';
import { ConnectionState, OnlineStateService } from 'services/online-state.service';
import { ISubscribePage } from 'pages/subscribe/subscribe-interface';
import { IUnsubscribePage } from 'pages/unsubscribe/unsubscribe-interface';
import { IAdminImpersonatedUserPage } from 'pages/admin-impersonated-user/admin-impersonated-user-interface';
import { SubscriptionManager } from 'services/subscription-manager.service';
import { NotificationService } from 'services/notification.service';

// how long the purge waits for pending telemetry to reach the server before giving up on it.
// short on purpose: the user is waiting on a spinner, and these events are about to be deleted.
const TELEMETRY_FLUSH_BEFORE_PURGE_TIMEOUT_MS = 5000;

@Component({
    selector: 'page-user-profile',
    templateUrl: 'user-profile.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfilePage extends CsPage<{}> {
  protected LL = LL;
  // refunds are Paddle-web-only: hide the Refund Policy footer link on native,
  // where Apple / Google handle payments and Paddle refunds do not apply.
  protected isWeb = !isCapacitorNative();
  protected userName = '';
  protected userEmail = '';
  protected avatarUrl = '';

  protected roleName: TranslatedString = untranslatedString('');
  protected roleInfo: TranslatedString = untranslatedString('');
  protected roleIcon = '';
  protected roleClass = '';
  protected roleIsTemporary = false;
  // The formatted date OUR ACCESS ends: the stored expiry as written, which on
  // most rails is the billing period end plus our 24h grace. It captions the
  // "Expires On" row and the "ends on" / "will end on" notes.
  //
  // The date the PROVIDER acts on is a separate question, answered a day earlier
  // on every graced rail, and the card computes it itself — see
  // `providerActsDate` in subscription-card-model.ts (#4278).
  protected licenseExpiry = '';
  /** The raw stored expiry behind {@link licenseExpiry}, passed to the card so
   *  both of its dates are derived from ONE value. */
  protected licenseExpiryIso: string | undefined;
  public canCancelSubscription = false;
  public trialEnds = '';
  loadingSlowData = true;
  subscriptionInfoError = false;
  hasPayProData = false;
  hasPaddleData = false;

  showSwitchPlanBanner = true;

  /**
   * Everything the subscription card shows, built by `buildSubscriptionCardModel`.
   *
   * Assigned in ONE place, `renderCard`, which is also the only place this page
   * marks itself for check. The card component is OnPush and redraws only when this
   * object is replaced, so every change this page draws on purpose arrives with a
   * fresh model. Do not call `cdr.markForCheck()` directly on this page — call
   * `renderCard()`, or the card will not see the change.
   *
   * It is a snapshot, not a live view: the login result and the clock are read at
   * the last rebuild, not on every redraw as the old getters did. The accepted
   * consequence is that a login result replaced from outside this page, or a coach
   * grant expiring while the page sits open, shows at the next rebuild rather than
   * on an unrelated redraw. See the component's own comment for why that is safe.
   *
   * Why one rebuild point matters even though the subscription service's fields
   * cannot currently change behind this page's back: the card used to stay current
   * only because every change to that shared state happened to be bracketed by a
   * `loadingSlowData` flip in `synchronizeSlowData`. Nothing wrote that down, so the
   * next person to refresh the subscription from an action handler would have
   * broken the card silently. Deferred finding df-41c084aa52fc.
   */
  protected subscriptionCard: SubscriptionCardModel;

  // read through UserDb, which reports a damaged or unreadable value once per page load (#5191)
  get impersonationUserAuth() {
    return this.userDb.getImpersonationUserAuth();
  }

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    public auth: AuthService,
    private alertCtrl: AlertController,
    private popupCtrl: PopupMenuProvider,
    private loadingCtrl: LoadingController,
    protected settings: SettingsService,
    protected imageService: ImageService,
    protected cloudFunctionsService: CloudFunctionsService,
    protected svPlanHandler: SvPlanHandlerService,
    public logging: LoggingService,
    public databaseRecovery: DatabaseRecoveryService,
    private telemetry: TelemetryService,
    public subManager: SubscriptionManager,
    private cdr: ChangeDetectorRef,
    private onlineState: OnlineStateService,
    private notificationService: NotificationService,
    private becomeCoachFlow: BecomeCoachFlowService,
    private toastCtrl: ToastController,
    private imageUploadOutbox: ImageUploadOutboxService,
    private nativeImageCache: NativeImageCacheService,
  ) {
    super();
    // built here rather than in a field initializer: the template binds it on
    // its first render, and the injected services it reads are only assigned
    // once the constructor runs.
    this.subscriptionCard = this.buildCard();
  }

  /**
   * Rebuild the subscription card from current state, then mark the page for
   * check. The one place either happens — see `subscriptionCard`.
   */
  private renderCard(): void {
    this.subscriptionCard = this.buildCard();
    this.safeMarkForCheck(this.cdr);
  }

  /** Assemble the card's inputs from this page and its services, and build it. */
  private buildCard(): SubscriptionCardModel {
    const card = buildSubscriptionCardModel({
      roleClass: this.roleClass,
      roleName: this.roleName,
      roleInfo: this.roleInfo,
      roleIsTemporary: this.roleIsTemporary,
      canCancelSubscription: this.canCancelSubscription,
      trialEnds: this.trialEnds,
      licenseExpiry: this.licenseExpiry,
      licenseExpiryIso: this.licenseExpiryIso,
      loadingSlowData: this.loadingSlowData,
      subscriptionInfoError: this.subscriptionInfoError,
      hasPayProData: this.hasPayProData,
      hasPaddleData: this.hasPaddleData,
      showSwitchPlanBanner: this.showSwitchPlanBanner,
      platform: getCapacitorPlatform(),
      now: DateTime.utc(),
      locale: BOOT_LOCALE,
      provider: this.subManager.provider,
      canSwitchPlan: this.subManager.canSwitchPlan,
      currentPlan: this.subManager.currentPlan,
      otherPlan: this.subManager.otherPlan,
      pendingPlanChange: this.subManager.pendingPlanChange,
      isPaymentFailing: this.subManager.isPaymentFailing,
      canManageSubscription: this.subManager.canManageSubscription,
      serverCanCancelSubscription: this.subManager.canCancelSubscription,
      serverCanResumeSubscription: this.subManager.canResumeSubscription,
      subscriptionStatus: this.subManager.subscriptionStatus,
      couch: this.auth.couch,
    });
    // every plan today is monthly or yearly (see subscribe-native-rail.ts's
    // recurrenceLabel for the same 1/12 assumption), so a plan of any other
    // length is an anomaly worth reporting. The card falls back to the plan's own
    // title rather than confidently mislabeling it "Yearly". Reported here, once
    // per rebuild, because the builder is pure and cannot report anything itself.
    if (card.unknownRecurrenceMonths !== null) {
      this.logging.trackSilentProblem('subscription-type-label-unknown-recurrence', { numMonths: card.unknownRecurrenceMonths });
    }
    return card;
  }

  populateUserDetails() {
    // if user logged out while on profile page, Sentry 3K1
    if (!this.auth.user) return;
    this.userName = this.auth.user.name;
    this.userEmail = this.auth.user.email;
    this.avatarUrl = this.auth.user.avatarUrl;

    let { role, expiry_utc } = this.auth.getAuthRoleAndExpiry();
    let roleDescription = describeAuthRoles().find(d => d.value === role);
    if (roleDescription) {
      this.roleName = roleDescription.name;
      this.roleInfo = roleDescription.userExplanation;
      this.roleIcon = roleDescription.icon;
      this.roleClass = roleDescription.id;
    }
    this.roleIsTemporary = this.auth.getSubscriptionStatus() === SubscriptionStatus.Temporary;
    this.canCancelSubscription = this.auth.getCanCancelSubscription();
    this.hasPayProData = !!(this.auth.couch && (this.auth.couch.payProSubscriptionId || this.auth.couch.payProProductId));
    this.hasPaddleData = !!(this.auth.couch && this.auth.couch.paddleData?.subscriptionId);
    // Work out the "free trial ends on" date to show. This covers Apple
    // and Google Play free trials (whose trial state lives in the
    // per-provider block) as well as the PayPro top-level field. The
    // helper only returns a date for a trial that is active right now, so
    // a finished trial is already dropped here. See issue #3791.
    const trialEndIso = resolveTrialEndIso(this.auth.couch, DateTime.utc().toISO());
    this.trialEnds = trialEndIso
      ? formatDateMedPadded(DateTime.fromISO(trialEndIso).toLocal(), BOOT_LOCALE)
      : '';
    // Handle license expiry - check for perpetual licenses
    this.licenseExpiryIso = expiry_utc;
    if (expiry_utc == undefined || expiry_utc === 'never') {
      this.licenseExpiry = '';
    } else {
      const expiryDate = DateTime.fromISO(expiry_utc);
      if (expiryDate.isValid) {
        // This is the ACCESS-ENDS answer: the stored value as written, which on
        // every graced rail is the billing period end plus our 24h grace. It is
        // what the "Expires On" row and the "ends on" / "will end on" notes ask.
        //
        // The card works out the PROVIDER-ACTS date — a day earlier on a graced
        // rail, the same day on Apple — from `licenseExpiryIso` beside this, so
        // both dates come from one stored value and cannot disagree about which
        // subscription they describe. Which sentence gets which is decided in
        // subscription-card-model.ts, never at the display site (#4278).
        this.licenseExpiry = formatDateMedPadded(expiryDate.toLocal(), BOOT_LOCALE);
      } else {
        // Invalid date means perpetual license
        this.licenseExpiry = 'Perpetual';
      }
    }

    // if the user is not premium user, that means the trial has expired, so the trial end time is not used
    if (role < AuthRole.Paid) {
      this.trialEnds = '';
      this.licenseExpiry = '';
      this.licenseExpiryIso = undefined;
    }

    this.renderCard();
  }
  async loadAsync() {
    // immediately populate the user details with current data (which might be outdated)
    this.populateUserDetails();
    // start re-logging-in the user and loading the prices in the background - they are slow to load, but non-essential to display the page
    void this.synchronizeSlowData();
  }

  // this is a slow function, so it's called in the background
  async synchronizeSlowData() {
    this.loadingSlowData = true;
    this.renderCard();

    // remember who this refresh is being made for, so the catch below can tell a genuine
    // failure from one that belongs to a session the app has already left. read it BEFORE
    // the first await: reading it in the catch would compare the signed-in user against
    // themselves and could never spot a switch.
    const uidAtStart = this.auth.user?.uid;

    try {
      /* first refresh the user's login - this improves the cases where the user is logged in for a long time
      and a temporary license expires in background */
      // wait for Firebase auth to settle — page can be entered during warm-resume Phase A
      // before fbAuth.currentUser is populated; attemptCouchLoginAsync would otherwise bail
      // with "no Firebase user" and surface a misleading 'Failed to re-login' error.
      await this.auth.whenFirebaseAuthSettled;
      if (this.viewDestroyed) return;
      let [loginSuccess] = await Promise.all([this.auth.attemptCouchLoginAsync(CouchLoginReason.ProfileLicenseRefresh), sleep(1000)]); // sleep to make sure the UI is not "glitching"
      if (this.viewDestroyed) return;
      if (!loginSuccess) {
        // if offline, failing to login is expected — not an error
        if (this.onlineState.connectionState !== ConnectionState.Online) {
          console.log('User profile: skipping slow data sync — user is offline');
        } else if (this.auth.isLikelySignOutRace()) {
          // user signed out during the await above — benign race, not a failure
          console.log('User profile: skipping slow data sync — user signed out during re-login');
        } else {
          this.logging.trackAbnormalError('Failed to re-login the user.');
        }
        return;
      }
      // after re-login, we can refresh the user data
      this.populateUserDetails();

      // fetch subscription info + plans via unified SubscriptionManager
      // guard: if the user signed out while we were awaiting the re-login above, bail
      if (!this.auth.user) return;
      await this.subManager.refreshAsync();
      if (this.viewDestroyed) return;

      // refreshAsync re-runs the server-side reconcile + re-login, which can
      // change the user's role (e.g. terminate the subscription if Apple
      // purged the record). Re-populate role-derived fields so the badge /
      // "current plan" copy stay in sync with the freshly-fetched auth.couch.
      this.populateUserDetails();
      // sync canCancelSubscription from subManager (server-authoritative)
      this.canCancelSubscription = this.subManager.canCancelSubscription;
    } catch (err) {
      this.subscriptionInfoError = true;
      // if the session changed under this refresh — the user signed out, or switched to a
      // different account — the auth error is expected and the new session runs its own
      // refresh, so don't report it
      if (!this.auth.isLikelySessionChangeRace(uidAtStart)) {
        this.logging.trackSilentProblem('Failed to refresh subscription info', err);
      }
    } finally {
      this.loadingSlowData = false;
      this.renderCard();
    }
  }

  async ionViewCanEnter() {
    try {
      await this.loadAsync();
    } catch (err) {
      this.logging.trackAbnormalException(err);
    }
  }

  async handleOptionsMenu(ev: Event) {
    let opts: PopupMenuOptions = { buttons: [], targetFromElement: ev.target as HTMLElement };

    // low-discoverability entry into the coach-onboarding flow (R6.1).
    // Platform-branched in the handler: store platforms show the
    // fixed-tier picker; web opens the Paddle coach-seat checkout.
    // Shown only where coach onboarding is actually
    // available for this platform — a mobile store that isn't configured
    // in the tier catalog (e.g. Apple before its products exist) hides
    // the entry entirely (the catalog read is cached, so this is cheap).
    if (await this.becomeCoachFlow.isAvailableAsync()) {
      opts.buttons.push({
        text: LL.coach.tier.becomeCoach(),
        icon: 'people',
        handler: () => {
          // the flow lives in a service, which has no view lifecycle of its own,
          // so it takes this page's destroyed check to decide whether it may
          // still open the support dialog after its own alert closes
          void this.becomeCoachFlow.startAsync(() => this.viewDestroyed);
        },
      });
    }

    opts.buttons.push({
      text: LL.profile.menu.deletePrivateData(),
      icon: 'warning',
      cssClass: 'danger',
      handler: () => {
        void this.deleteProfileAsync();
      },
    });
    void this.popupCtrl.createMenu(opts);
  }

  handleSignOut() {
    void getMyApp().logoutAsync();
  }

  onImpersonationIndicatorClicked() {
    void this.navCtrl.pushAsync(IAdminImpersonatedUserPage, {});
  }

  onBannerDismissed() {
    this.showSwitchPlanBanner = false;
    this.renderCard();
  }

  async deleteProfileAsync() {
    // if user is currently being coached, offer to leave coaching first
    const coachRel = this.userDb.loadCoachRelAsClient();
    if (coachRel?.coachUid) {
      const action = await this.alertCtrl.showAlertAsync<'leave' | undefined>({
        title: LL.profile.deleteData.title(),
        message: LL.coach.deleteBlocked.message({ coachName: coachRel.coachName }),
        buttons: [
          { text: LL.coach.selfDetach.deleteAccountButton(), role: AlertButtonRole.Primary, result: 'leave' },
          { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
        ],
      });
      if (action !== 'leave') return;

      // disconnect from coach first, then continue with deletion
      try {
        await this.cloudFunctionsService.coachClientSelfDetachAsync({});
      } catch (err) {
        console.error('deleteProfileAsync: self-detach failed:', err);
        await this.alertCtrl.showAlertAsync({
          title: LL.coach.selfDetach.confirmTitle(),
          message: LL.common.errors.generic(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true }],
        });
        return;
      }
    }

    let action = await this.alertCtrl.showAlertAsync<'delete' | undefined>({
      title: LL.profile.deleteData.title(),
      message: LL.profile.deleteData.message(),
      buttons: [
        { text: LL.profile.deleteData.deleteButton(), role: AlertButtonRole.Primary, result: 'delete' },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
      ],
    });
    if (action === 'delete') {
      await this.purgeAccountAsync();
    }
  }

  async purgeAccountAsync() {
    let loading = this.loadingCtrl.create({ content: LL.profile.deleteData.deleting() });
    void loading.present();
    try {
      // flush pending telemetry before the purge destroys the database, but BOUND the wait:
      // flushAsync() never settles while the server-side flush keeps failing (see
      // design-docs/telemetry.md — the flusher retries forever), so an unbounded await here
      // let a degraded server wedge the spinner and leave the data undeleted.
      // abandoning the wait leaves the flusher running against a db we are about to destroy.
      // that is tolerated, not ignored: the purge suppresses post-destroy idb errors and the
      // flusher's db touchpoints are isInitialized()-guarded (telemetry.md records the one
      // narrow check-then-use race that remains).
      try {
        await timeout(TELEMETRY_FLUSH_BEFORE_PURGE_TIMEOUT_MS, this.telemetry.flushAsync());
      } catch (err) {
        // report it, but keep purging — a stuck telemetry flush must never be what stops the
        // user's deletion from happening. losing these events is fine, they are about to go anyway.
        this.logging.trackSilentProblem('PurgeTelemetryFlushFailed', errorReportFields(err));
      }
      // cancel all pending local notifications
      await this.notificationService.cancelAllAsync();
      await this.deleteScanPhotosAsync();
      await this.userDb.database.purgeDatabaseAsync();
      await this.clearQueuedPhotoUploadsAsync();
      await this.clearCachedPhotosAsync();
      await this.settings.resetToDefaultsAsync(true); // Skip database sync since database was just purged
    } catch (err) {
      void loading.dismiss();
      // if the local database itself broke, hand it to recovery and stop. trackAbnormalException
      // used to run this check for us as its first step, and the report below does not. the note
      // goes with it: graceful recovery reports through plain trackEvent, so nothing else here
      // would stop us asking this person to rate the app right after a real fault (#4625).
      if (this.databaseRecovery.interceptDatabaseError(err)) {
        if (!this.databaseRecovery.isIntentionalDestruction()) noteSomethingWentWrong();
        return;
      }
      // report and tell the user as two separate steps. trackAbnormalException would do both, but
      // Sentry's beforeSend drops the transient fetch error a mid-purge network change throws, and
      // a dropped event silences the message too. See design-docs/telemetry.md, "A beforeSend
      // filter can silence a report".
      this.logging.trackSilentProblem('AccountPurgeFailed', errorReportFields(err));
      await this.alertCtrl.showAlertAsync({
        message: LL.profile.deleteData.error(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true }],
      });
      return; // don't logout if didn't delete completely
    }

    await getMyApp().logoutAsync();
    // only stop this loader after the logout one is created
    void loading.dismiss();
  }

  /**
   * Ask the server to delete this person's AI scan photos and the AI's answers
   * (#5633), and the photos they uploaded themselves except those a published food
   * or exercise still shows (#5939). They live outside the user's own database, so
   * the purge below cannot reach them. The server needs that database to tell which
   * photos are published, which is one more reason this runs first.
   *
   * Runs BEFORE the purge and throws when the server could not finish: the purge
   * then stops with its usual error, rather than telling the person their
   * data is gone while their photos are still stored. Running it again is safe.
   *
   * Skipped while an admin impersonates somebody: the database here is the other
   * person's, but the server call carries the ADMIN's sign-in, so it would delete
   * the admin's own scans instead.
   */
  private async deleteScanPhotosAsync(): Promise<void> {
    if (this.impersonationUserAuth) {
      return;
    }
    const result = await this.cloudFunctionsService.deleteMyScanDataAsync({});
    if (!result?.complete) {
      throw new Error('the server could not delete every scan photo');
    }
  }

  /**
   * Throw away every photo still waiting to be uploaded, as part of the purge.
   *
   * Called from inside `purgeAccountAsync`'s try, AFTER the database purge has
   * succeeded — the same placement, for the same reason, as the marker clear
   * `purgeDatabaseAsync` does at the end of its own try: a purge that FAILED
   * leaves the user signed in with their data intact, where a queued photo is
   * still real and must stay. What goes wrong without this, and why the photo is
   * dropped rather than uploaded, is in `design-docs/sync-integrity.md`, "The
   * change-tracking flags".
   *
   * A failure here is reported and swallowed. By this point the user's data has
   * already been deleted from the server and from this device; a failed outbox
   * clear must never be what stops the deletion completing — the same trade, and
   * the same handling, as the bounded telemetry flush at the top of the purge.
   */
  private async clearQueuedPhotoUploadsAsync(): Promise<void> {
    try {
      const { removed, failed } = await this.imageUploadOutbox.clearAllQueuedAsync();
      // if any item survived, say so at problem tier. It is not merely a stray
      // file: the outbox is still non-empty, so the sign-out guard puts its
      // "Sign Out Without Uploading?" dialog back — the very thing this method
      // exists to prevent — and nothing else would report why.
      if (failed > 0) {
        this.logging.trackSilentProblem('PurgeQueuedPhotoClearIncomplete',
          { discardedPhotoCount: removed, failedPhotoCount: failed });
        return;
      }
      // stay quiet when there was nothing queued, which is the ordinary case — a
      // report on every deletion is the noise that makes the real ones unreadable.
      // Info tier, NOT problem: the bytes were destroyed because the user asked
      // for them to be, so nothing is wrong and nobody needs to look. It reaches
      // Mixpanel through the `sentry_error` event that every silent report emits,
      // so the volume is visible without a new event name.
      if (removed > 0) {
        this.logging.trackSilentInfo('PurgeDiscardedQueuedPhotos', { discardedPhotoCount: removed });
      }
    } catch (err) {
      this.logging.trackSilentProblem('PurgeQueuedPhotoClearFailed', errorReportFields(err));
    }
  }

  /**
   * Delete the copies of the user's photos the native apps keep for offline use (#5692). Like the
   * outbox clear above, a failure is reported and swallowed: the data is already deleted, and a
   * leftover cache file must not be what stops the deletion completing.
   */
  private async clearCachedPhotosAsync(): Promise<void> {
    try {
      await this.nativeImageCache.clearAsync();
    } catch (err) {
      this.logging.trackSilentProblem('PurgeImageCacheClearFailed', errorReportFields(err));
    }
  }

  gotoPurchase() {
    void this.navCtrl.pushAsync(ISubscribePage, {});
  }

  async onReportProblemButtonClicked() {
    this.telemetry.trackEvent(`user_profile_report_problem`, {});
    // #4625 — remember locally that this person told us something was wrong, so
    // we stay quiet about rating the app for a while. The telemetry event above
    // cannot serve for this: the app has no way to read its own telemetry back.
    // Recorded on the TAP rather than on a completed report, because this action
    // only opens a dialog with the support address — there is no later moment
    // that tells us whether they went on to write to us.
    //
    // The failure is reported and swallowed, never allowed to reach the dialog
    // below. That save writes the synced settings document, so it really can
    // throw — and this action is the ONLY route in the app to the support
    // address, so letting it through would mean somebody taps "report a problem"
    // and nothing at all appears. Losing this marker costs at worst one rating
    // prompt shown at an unwelcome moment. Same trade, same handling, as the
    // sibling site in send-feedback.ts.
    try {
      await this.settings.recordProblemReportedAsync();
    } catch (err) {
      this.logging.trackSilentProblem('ReviewPromptProblemMarkerSaveFailed',
        errorReportFields(err));
    }
    // show a dialog with the support address instead of a bare "mailto:" navigation,
    // which is silently swallowed on a desktop with no default mail client (#3830)
    await presentSupportContactAsync(this.alertCtrl, this.toastCtrl, this.auth.user.email);
  }

  // if relative URL, prepend server origin (on Capacitor native, bare paths resolve to localhost)
  openLink(url: string) {
    const fullUrl = url.startsWith('/') ? getSharedUrlOrigin() + url : url;
    window.open(fullUrl, '_blank');
  }

  async onResumeClick() {
    // A PAUSED Paddle sub is resumable from LOCAL state alone, so it must not be
    // sent to the Subscribe page: `canResumeSubscription` comes from
    // getSubscriptionInfo, and on the transient-failure path it is false even
    // though the resume would work. Sending them to Subscribe would be a loop —
    // that page now tells a paused member to "resume from your profile" and
    // hides every purchase surface. Resuming needs no new payment details
    // either: the subscription and its payment method are still on file.
    if (!this.subManager.canResumeSubscription && !isPausedPaddleSubscriber(this.auth.couch)) {
      // we can't resume it, but must ask the user to enter new payment info - by going to the subscription page
      await this.navCtrl.pushAsync(ISubscribePage, {});
      return;
    }

    // Apple: parallel to onCancelClick's apple branch — present the native
    // StoreKit Manage Subscriptions sheet (in-app) where the user can toggle
    // auto-renew back on; resumeAsync resolves on sheet dismissal. The
    // "success" alert is optimistic (Apple's sheet reports nothing about
    // whether they actually toggled), matching the cancel-side copy. After
    // dismissal the inline reloadAppAsync re-runs refreshAsync; the
    // server-side canResumeSubscription flips back to false and the UI
    // returns to the REGULAR-ACTIVE block automatically.
    if (this.subManager.provider === 'apple') {
      let confirm = await this.alertCtrl.showAlertAsync({
        message: LL.profile.appleResume.confirm(),
        buttons: [
          { text: LL.profile.appleResume.confirmButton(), role: AlertButtonRole.Primary, result: true },
          { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        ],
      });
      if (!confirm) return;

      let loading = this.loadingCtrl.create({});
      await loading.present();
      try {
        let result = await this.subManager.resumeAsync();
        if (result.error) {
          this.logging.trackAbnormalError(result.error, LL.profile.appleResume.error());
          return;
        }
        await this.alertCtrl.showAlertAsync({
          message: LL.profile.appleResume.success(),
          buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
        });
        await getMyApp().reloadAppAsync({ resetToRoot: false });
      } catch (error) {
        this.logging.trackAbnormalException(error, LL.profile.appleResume.error());
      } finally {
        await loading.dismiss();
      }
      return;
    }

    // A PAUSED subscription gets its own confirm, because resuming one can take
    // money right now. Once the paid period has lapsed the server omits
    // `on_resume`, so Paddle applies its default `start_new_billing_period` and
    // charges the saved card immediately. The plain "resume?" copy is fine for
    // the other case this button serves - undoing a scheduled cancel, which only
    // removes the pending change and bills nothing.
    //
    // The copy states the charge outright and then qualifies it ("unless you
    // still have paid time left"), rather than the other way round. A pause
    // normally starts when the paid period runs out, so by the time a sub is
    // Paused the charge is the near-certain outcome; hiding it behind an "if"
    // would put the likely case in the reader's blind spot on a consent screen.
    // The trailing qualifier keeps it truthful for the rare immediate pause,
    // which can leave paid time on the clock and then costs nothing.
    //
    // It is worded as a qualifier rather than resolved to a definite yes/no on
    // purpose: deciding which case applies means redoing the server's
    // grace-subtracted date comparison on the client, where the two copies would
    // silently drift apart.
    let confirm = await this.alertCtrl.showAlertAsync({
      message: isPausedPaddleSubscriber(this.auth.couch) ? LL.profile.resubscribe.confirmPaused() : LL.profile.resubscribe.confirm(),
      buttons: [
        { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
      ],
    });
    if (!confirm) return;

    let loading = this.loadingCtrl.create({});
    await loading.present();
    try {
      let result = await this.subManager.resumeAsync();
      if (result.error) {
        this.logging.trackAbnormalError(result.error, LL.errors.userFacing.resubscribeFailed());
        return;
      }

      await this.alertCtrl.showAlertAsync({
        message: LL.profile.resubscribe.success(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });

      await getMyApp().reloadAppAsync({ resetToRoot: false });
    } catch (error) {
      this.logging.trackAbnormalError(error, LL.errors.userFacing.resubscribeFailed());
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Server error codes that mean "your page is out of date", not "something
   * broke". They are reachable without any bug: the Paddle-hosted portal that
   * the Manage Subscription button opens can schedule a change in another tab,
   * after which a Switch tapped on this stale page is correctly refused.
   * Routing them through trackAbnormalError would show a wrong generic failure
   * AND file a Sentry abnormal-error for an expected precondition.
   *
   * `switch_price_not_allowed` is deliberately NOT in this list: the stale-tab
   * race it looks like is already absorbed by the no-op success when the price
   * is unchanged, so the reachable causes are a legacy/unmapped price or a
   * tampered caller — neither of which is "your page is out of date", and both
   * of which we want reported.
   */
  private static readonly kStalePageErrorCodes = new Set([
    'change_already_scheduled',
    'switch_not_allowed_while_paused',
  ]);

  /** Shows the "reopen this page" message for a stale-state code; returns
   *  whether it handled the error. */
  private async handleStalePageErrorAsync(errorCode: string): Promise<boolean> {
    if (!UserProfilePage.kStalePageErrorCodes.has(errorCode)) return false;
    await this.alertCtrl.showAlertAsync({
      message: LL.profile.paddleStaleState(),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
    return true;
  }

  /**
   * The message shown after a cancel succeeds.
   *
   * A method with a switch rather than a chain of ternaries, because the cases
   * differ per provider AND, within Paddle, per paused state, and each needs a
   * sentence of explanation that has nowhere to live inside an expression.
   *
   * `alreadyScheduled` is the #4629 case: the server found that Paddle had this
   * cancellation scheduled already, so nothing new was requested. It is still a
   * success — the customer IS cancelled — but "your subscription has been
   * canceled" would imply we just did it, so it gets its own wording.
   */
  private cancelSuccessMessage(alreadyScheduled: boolean): TranslatedString {
    switch (this.subManager.provider) {
      case 'apple':
        return LL.profile.appleCancel.success();

      case 'paddle':
        // Paddle already had it scheduled. Checked BEFORE the paused test,
        // because it describes what the server actually found and that is true
        // whatever state the local (possibly stale) record is in.
        if (alreadyScheduled) return LL.profile.paddleCancel.alreadyScheduled();
        // cancelling an already-PAUSED sub takes effect immediately, so there is
        // no remaining access to promise — and `licenseExpiry` is blank for a
        // paused user anyway (their role is below Paid), which would render
        // "You'll retain access until ." with an empty date.
        if (isPausedPaddleSubscriber(this.auth.couch)) return LL.profile.paddleCancel.successImmediate();
        // Deliberately quotes NO date. The scheduled cancel is written
        // server-side and has not necessarily synced back to the local session
        // yet at this moment, so `licenseExpiry` here can still be the OLD value
        // (or blank) rather than the new end date. Naming the period instead
        // ("until the end of your current billing period") is true regardless,
        // and after the reload the "ends on" note carries the precise graced
        // date — the same value the whole card now shows.
        return LL.profile.paddleCancel.successScheduled();

      default:
        return LL.profile.googlePlayCancel.success({ date: this.licenseExpiry });
    }
  }

  async onCancelClick() {
    // if provider needs cancellation reason, navigate to unsubscribe page
    if (this.subManager.needsCancellationReason) {
      void this.navCtrl.pushAsync(IUnsubscribePage, {});
      return;
    }

    const cancelStrings = this.subManager.provider === 'apple'
      ? LL.profile.appleCancel
      : this.subManager.provider === 'paddle'
        ? LL.profile.paddleCancel
        : LL.profile.googlePlayCancel;

    // cancelling an already-PAUSED Paddle sub takes effect immediately, so the
    // standard "you'll keep access until the end of your billing period" promise
    // is false for them — and this is the message they read BEFORE agreeing.
    const cancelConfirmMessage = isPausedPaddleSubscriber(this.auth.couch)
      ? LL.profile.paddleCancel.confirmImmediate()
      : cancelStrings.confirm();

    let confirm = await this.alertCtrl.showAlertAsync({
      message: cancelConfirmMessage,
      buttons: [
        { text: cancelStrings.confirmButton(), role: AlertButtonRole.Primary, result: true },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
      ],
    });
    if (!confirm) return;

    let loading = this.loadingCtrl.create({});
    await loading.present();
    try {
      let result = await this.subManager.cancelAsync();
      if (result.error) {
        this.logging.trackAbnormalError(result.error, cancelStrings.error());
        return;
      }

      // `alreadyScheduled` arrives WITHOUT an error, so it takes this success
      // path unchanged — alert, then reload. Only the wording differs. Reloading
      // is what actually answers "when does it end?": the server has just brought
      // our stored copy back in step with Paddle, so the reloaded card shows the
      // real end date and offers Resume in place of a Cancel that would fail
      // again. (#4629)
      const successMessage = this.cancelSuccessMessage(!!result.alreadyScheduled);
      await this.alertCtrl.showAlertAsync({
        message: successMessage,
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });

      await getMyApp().reloadAppAsync({ resetToRoot: false });
    } catch (error) {
      this.logging.trackAbnormalException(error, cancelStrings.error());
    } finally {
      await loading.dismiss();
    }
  }

  async onManageSubscriptionClick() {
    // Paddle: open the hosted customer portal (payment method + invoices)
    // instead of PayPro's email-a-login-link model.
    if (this.subManager.provider === 'paddle') {
      await this.openPaddlePortalAsync();
      return;
    }

    let loading = this.loadingCtrl.create({});
    await loading.present();

    try {
      let result = await this.cloudFunctionsService.userSubscriptionAsync({
        action: 'SendLoginEmail',
      });

      if (result.error) {
        this.logging.trackAbnormalError(result.error, LL.errors.userFacing.sendLoginEmailFailed());
        return;
      }

      await this.alertCtrl.showAlertAsync({
        message: LL.profile.manageSubscription.emailSent(),
        buttons: [
          {
            text: LL.common.buttons.ok(),
            role: AlertButtonRole.Primary,
          },
        ],
      });

    } catch (error) {
      this.logging.trackAbnormalException(error, LL.errors.userFacing.sendLoginEmailFailed());
    } finally {
      await loading.dismiss();
    }
  }

  // Open the Paddle customer portal in a browser. The server mints a
  // short-lived portal-session URL (payment method + invoices); we open it and
  // never persist it. A failure surfaces a translated, actionable error rather
  // than a silent no-op.
  private async openPaddlePortalAsync() {
    let loading = this.loadingCtrl.create({});
    await loading.present();
    try {
      const result = await this.cloudFunctionsService.paddleManageSubscriptionAsync({
        action: 'PortalSession',
      });
      if (result.error || !result.url) {
        this.logging.trackAbnormalError(result.error ?? 'no_url', LL.errors.userFacing.paddlePortalFailed());
        return;
      }
      // open the Paddle-hosted portal in a new tab / the system browser
      window.open(result.url, '_blank');
    } catch (error) {
      this.logging.trackAbnormalException(error, LL.errors.userFacing.paddlePortalFailed());
    } finally {
      await loading.dismiss();
    }
  }

  async onSwitchPlanClick() {
    // if user is on trial, show informational alert instead of attempting the switch
    if (this.trialEnds) {
      await this.alertCtrl.showAlertAsync({
        message: LL.profile.switchPlan.trialNotSupported(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return;
    }

    const otherPlan = this.subManager.otherPlan;
    // Paddle plans carry no priceText (see PaddleSubscriptionStrategy.loadPlans), so use copy that
    // does not quote a price. Note there is NO checkout for a plan switch: the
    // switch PATCHes the subscription with prorated_immediately, so the card is
    // charged (or credited) straight away. The confirmNoPrice copy says exactly
    // that instead of promising a price screen the user will never see.
    const switchConfirmMessage = otherPlan.priceText
      ? LL.profile.switchPlan.confirm({ plan: otherPlan.title, price: otherPlan.priceText })
      : LL.profile.switchPlan.confirmNoPrice({ plan: otherPlan.title });
    let confirm = await this.alertCtrl.showAlertAsync({
      message: switchConfirmMessage,
      buttons: [
        { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: true },
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
      ],
    });
    if (!confirm) return;

    let loading = this.loadingCtrl.create({});
    await loading.present();
    try {
      let result = await this.subManager.switchPlanAsync();

      if (result.canceled) return;

      if (result.error) {
        // if server detected trial subscription
        if (result.error === 'trial_upgrade_not_supported') {
          await this.alertCtrl.showAlertAsync({
            message: LL.profile.switchPlan.trialNotSupported(),
            buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
          });
        } else if (result.error === 'legacy_price_grant_would_be_lost') {
          // This customer was granted the price they used to pay on PayPro
          // (#5084). That grant is a discount tied to the plan they are on, so
          // switching would silently put them back on the full price. Support
          // can switch them and grant again on the new plan, so point them
          // there rather than leaving them at a dead end.
          //
          // Not reported as an error: this is an expected refusal on a
          // deliberate, correct rule, exactly like the trial branch above.
          await this.alertCtrl.showAlertAsync({
            message: LL.profile.switchPlan.grantedPriceWouldBeLost(),
            buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
          });
        } else if (result.confirmationFailedAfterPurchase) {
          // the store made the change and our confirmation never got an answer,
          // so we do not know that anything failed — and saying so to somebody
          // the store has just charged invites them to pay again. Report it, but
          // report it silently: the loud path shows the user its own message.
          this.logging.trackSilentProblem('SwitchPlanNotConfirmed', { error: result.error });
          await this.alertCtrl.showAlertAsync({
            message: LL.profile.switchPlan.notYetConfirmed(),
            buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
          });
        } else if (!await this.handleStalePageErrorAsync(result.error)) {
          // handleStalePageErrorAsync both SHOWS the alert and reports whether it
          // owned the error, so negating it leaves only the genuine failures to
          // report. Written this way rather than as an empty "handled" branch.
          this.logging.trackAbnormalError(result.error, LL.errors.userFacing.planSwitchFailed());
        }
        return;
      }

      this.telemetry.trackEvent('switch_subscription', {
        old_months: this.subManager.currentPlan.numMonths,
        new_months: otherPlan.numMonths,
      });

      // Apple downgrade: server's appleValidate already wrote
      // appleData.autoRenewProductId so SubscriptionManager.pendingPlanChange
      // reflects the queue on the next refresh — here we only pick the right
      // immediate-confirm copy (scheduled vs immediate).
      // "...when your current subscription cycle ends on {date}" names the moment
      // the STORE switches the plan, so it takes the provider date — the same one
      // the card's own "scheduled to switch" note shows, for the same event.
      //
      // This is consistency, not a live fix: the alert is reachable on APPLE only
      // (only the Apple strategy supplies `isScheduled` — see
      // subscription-strategy.ts), and Apple stores a raw expiry, so the two
      // dates coincide for everyone who can see this message. Asking the right
      // question here keeps it that way if another rail ever schedules a switch
      // (#4278).
      const message = result.scheduled
        ? LL.profile.switchPlan.scheduledConfirm({
          plan: otherPlan.title, date: this.subscriptionCard.planChangeNoteDate,
        })
        : LL.profile.switchPlan.success();
      await this.alertCtrl.showAlertAsync({
        message,
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });

      await getMyApp().reloadAppAsync({ resetToRoot: false });
    } catch (error) {
      this.logging.trackAbnormalException(error, LL.profile.switchPlan.error());
    } finally {
      await loading.dismiss();
    }
  }
}

